import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import "./styles.scss";
import { settingServices } from "Utils/setting/services";
import FieldInput from "./FieldInput";
import snackbarUtil from "Utils/snackBarUtil";
import { Modal, Button, Form } from "react-bootstrap";
import AddNewMarket from "./AddNewMarketModal";

type MarketSettingsModalProps = {
  onClose: () => void;
  data: {
    series_id: string;
    sport_id: string;
  };
  seriesData: any;
};


type MarketFormValues = {
  market_min_stack: string;
  market_max_stack: string;
  market_min_odds_rate: string;
  market_max_odds_rate: string;
  market_advance_bet_stake: string;
  market_max_profit: string;
  market_before_inplay_profit: string;
  market_bet_delay: string;
};

type FancyFormValues = {
  session_min_stack: string;
  session_max_stack: string;
  session_max_profit: string;
};

const marketFields = [
  { label: "Min. Stake", name: "market_min_stack" },
  { label: "Max. Stake Amount", name: "market_max_stack" },
  { label: "Min. Odds Rate", name: "market_min_odds_rate" },
  { label: "Max. Odds Rate", name: "market_max_odds_rate" },
  { label: "Before Inplay Match Stake", name: "market_advance_bet_stake" },
  { label: "Market Max Profit", name: "market_max_profit" },
  { label: "Before Inplay Profit", name: "market_before_inplay_profit" },
  { label: "Match Bets Delay", name: "market_bet_delay" }
];

const fancyFields = [
  { label: "Min. Stake", name: "session_min_stack" },
  { label: "Max. Stake Amount", name: "session_max_stack" },
  { label: "Session Max Profit", name: "session_max_profit" }
];

const MarketSettingsModal: React.FC<MarketSettingsModalProps> = ({ onClose, seriesData, data }) => {
  const [activeTopTab, setActiveTopTab] = useState<string>("markets");
  const [activeTab, setActiveTab] = useState<string>("");

  const [marketFormValues, setMarketFormValues] = useState<MarketFormValues>({
    market_min_stack: "",
    market_max_stack: "",
    market_min_odds_rate: "",
    market_max_odds_rate: "",
    market_advance_bet_stake: "",
    market_max_profit: "",
    market_before_inplay_profit: "",
    market_bet_delay: ""
  });

  const [fancyFormValues, setFancyFormValues] = useState<FancyFormValues>({
    session_min_stack: "",
    session_max_stack: "",
    session_max_profit: ""
  });

  const [initialMarketValues, setInitialMarketValues] = useState<MarketFormValues>(marketFormValues);
  const [initialFancyValues, setInitialFancyValues] = useState<FancyFormValues>(fancyFormValues);

  const [updatedFieldsState, setUpdatedFieldsState] = useState<
    Partial<MarketFormValues> | Partial<FancyFormValues>
  >({});

  const [marketWiseData, setMarketWiseData] = useState<any>(null);

  const [showAddMarketModal, setShowAddMarketModal] = useState(false);


  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (activeTopTab === "markets") {
      setMarketFormValues((prev) => ({ ...prev, [name]: value }));
      if (value !== initialMarketValues[name as keyof MarketFormValues]) {
        setUpdatedFieldsState((prev) => ({ ...prev, [name]: value }));
      } else {
        setUpdatedFieldsState((prev) => {
          const copy = { ...prev };
          delete (copy as any)[name];
          return copy;
        });
      }
    } else {
      setFancyFormValues((prev) => ({ ...prev, [name]: value }));
      if (value !== initialFancyValues[name as keyof FancyFormValues]) {
        setUpdatedFieldsState((prev) => ({ ...prev, [name]: value }));
      } else {
        setUpdatedFieldsState((prev) => {
          const copy = { ...prev };
          delete (copy as any)[name];
          return copy;
        });
      }
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  const getMarketWiseLimit = async () => {
    try {
      const payload = {
        series_id: data?.series_id,
        sport_id: data?.sport_id
      };
      const { response } = await settingServices.getMarketWiseLimites(payload);
      if (response) {
        setMarketWiseData(response?.data);
        const defaultTabKey = Object.keys(response?.data)[0];
        setActiveTopTab(defaultTabKey);
        const defaultInnerTab = response.data[defaultTabKey]?.[0];
        setActiveTab(defaultInnerTab?.market_name || defaultInnerTab?.category);
      }
    } catch (error) {
      console.error("Error fetching market data", error);
    }
  };

  const updateMarketWiseLimit = async () => {
    const checkUpdateValueLength = Object.keys(updatedFieldsState).length > 0;

    try {
      if (checkUpdateValueLength) {
        const checkMarket: any = {
          markets: "market",
          fancy: "fancy"
        };
        const payload = {
          event_type: checkMarket[activeTopTab],
          market_name: activeTab,
          series_id: seriesData?.series_id,
          series_name: seriesData?.name,
          setting_get_from: "series",
          sport_id: seriesData?.sport_id,
          values: updatedFieldsState
        };
        const { response } = await settingServices.UpdateMarketWiseLimites(payload);
        if (response.status) {
          snackbarUtil.success(response?.msg);
          onClose();
        } else if (response?.status === false) {
          snackbarUtil.error(response?.msg);
        }
      } else {
        snackbarUtil.error("Must change at least one field to proceed further");
      }
    } catch (error) {
      console.error("Error updating data", error);
    }
  };

  const loadFormValues = () => {
    if (!marketWiseData || !activeTopTab || !activeTab) return;
    const tabData = marketWiseData[activeTopTab]?.find(
      (item: any) => item.market_name === activeTab || item.category === activeTab
    );
    if (!tabData) return;

    if (activeTopTab === "markets") {
      const values: MarketFormValues = {
        market_min_stack: tabData.market_min_stack || "",
        market_max_stack: tabData.market_max_stack || "",
        market_min_odds_rate: tabData.market_min_odds_rate || "",
        market_max_odds_rate: tabData.market_max_odds_rate || "",
        market_advance_bet_stake: tabData.market_advance_bet_stake || "",
        market_max_profit: tabData.market_max_profit || "",
        market_before_inplay_profit: tabData.market_before_inplay_profit || "",
        market_bet_delay: tabData.market_bet_delay || ""
      };
      setMarketFormValues(values);
      setInitialMarketValues(values);
    } else {
      const values: FancyFormValues = {
        session_min_stack: tabData.session_min_stack || "",
        session_max_stack: tabData.session_max_stack || "",
        session_max_profit: tabData.session_max_profit || ""
      };
      setFancyFormValues(values);
      setInitialFancyValues(values);
    }

    setUpdatedFieldsState({});
  };

  useEffect(() => {
    getMarketWiseLimit();
  }, [data]);

  useEffect(() => {
    loadFormValues();
  }, [marketWiseData, activeTopTab, activeTab]);

  const renderFields = () => {
    const currentFields = activeTopTab === "markets" ? marketFields : fancyFields;
    const currentValues = activeTopTab === "markets" ? marketFormValues : fancyFormValues;

    return currentFields.map((field) => (
      <FieldInput
        key={field.name}
        label={field.label}
        name={field.name}
        value={currentValues[field.name as keyof typeof currentValues]}
        onChange={handleChange}
      />
    ));
  };
const marketName:any = {
  "markets":"Markets",
  "fancy":"Fancy"
}

  return (
    <>
      <div className="modal-content series-wise-modal-content">
        <div className="modal-header" style={{ backgroundColor: "#2370b0", color: "#fff" }}>
          <label className="modal-title">
          Settings:{seriesData?.name}
          </label>
          <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
        </div>

        <div className="sport-setting-body">
          <div className="limitSettingsTabs">
            {marketWiseData &&
              Object.keys(marketWiseData).map((tabKey) => (
                <button
                  key={tabKey}
                  className={activeTopTab === tabKey ? "active" : ""}
                  onClick={() => {
                    setActiveTopTab(tabKey);
                    const firstInner = marketWiseData[tabKey]?.[0];
                    setActiveTab(firstInner?.market_name || firstInner?.category);
                  }}
                >
                  {marketName[tabKey]}
                </button>
              ))}
          </div>

          <div className="body-inner">
            <div className="filter-tab">
              <nav>
                <div className="nav nav-tabs" role="tablist">
                  {marketWiseData &&
                    marketWiseData[activeTopTab]?.map((tab: any) => {
                      const tabLabel = tab.market_name || tab.category;
                      return (
                        <a
                          key={tabLabel}
                          className={`nav-link ${activeTab === tabLabel ? "active" : ""}`}
                          role="tab"
                          onClick={() => setActiveTab(tabLabel)}
                          style={{ cursor: "pointer" }}
                        >
                          {tabLabel}
                        </a>
                      );
                    })}
                </div>
              </nav>

              <div className="tab-content">
                <div className="tab-pane fade show active">
                  <form onSubmit={handleSubmit} noValidate>
                    <div className="sports-setting-main-section">
                      <div className="market-session-setting">
                        <div className="detail-row">
                          <div className="detail-row-heading">
                            <h4>
                              {activeTab}
                              <span className="yellowColor">
                                (The system default values are currently set. Please save them for the first time to see the reflection.)
                              </span>
                            </h4>
                          </div>
                          <div className="row">{renderFields()}</div>
                        </div>
                      </div>
                      <hr />
                      <div className="series-wise-modal-footer justify-content-between">
                        <button type="button" className="btn btn-primary" onClick={() => setShowAddMarketModal(true)}>
                          Add {activeTopTab}
                        </button>
                        <div className="text-end">
                          <button type="submit" className="btn btn-success" onClick={() => updateMarketWiseLimit()}>
                            Save
                          </button>
                          &nbsp;
                          <button type="button" className="btn btn-danger" onClick={onClose}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Market Modal */}
    <AddNewMarket
    showAddMarketModal={showAddMarketModal}
    setShowAddMarketModal={setShowAddMarketModal}
    activeTopTab={activeTopTab}
    data={data}
    />

    </>
  );
};

export default MarketSettingsModal;
